import { Box, Button } from '@chakra-ui/react';
import { MouseEventHandler, ReactNode, forwardRef } from 'react';

import sizes from 'styles/theme/sizes';

// Purple calendar-panel palette for the Dimensions table only. `datePickerStyle`
// (components/DatePicker/style.ts) is shared, unconditionally, by 8 other
// date-pickers across the app — not even gated by isRevampTable — so it's
// duplicated here rather than modified in place.
export const modernDatePickerStyle = {
  '& .react-datepicker-popper': {
    '& .react-datepicker': {
      width: sizes.full,
      boxShadow: '0px 8px 24px rgba(18, 24, 38, 0.12)',
      minWidth: '137px',
      borderRadius: '12px',
      border: '1px solid #E8E9EB',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      '& .react-datepicker__navigation': {
        top: sizes[2],

        '& .react-datepicker__navigation-icon:before': {
          borderColor: '#ffffff',
        },
      },
    },
    '& .react-datepicker__triangle::after': {
      left: '30px !important',
    },
    '& .react-datepicker__triangle::before': {
      left: '30px !important',
    },
  },

  '& .react-datepicker__header': {
    backgroundColor: '#8B5CF6 !important',
    color: '#ffffff !important',
  },

  '& .react-datepicker__current-month, & .react-datepicker-year-header': {
    color: '#ffffff !important',
  },

  '& .react-datepicker__month-container': {
    width: sizes.full,

    '& .react-datepicker__month-wrapper': {
      display: 'flex',
      justifyContent: 'space-around',

      div: {
        padding: `${sizes[2]} ${sizes[4]}`,
        borderRadius: sizes[2],
        color: '#121826',
        fontWeight: 'normal',
        backgroundColor: 'transparent',
      },
    },

    '& .react-datepicker__month-wrapper div[aria-selected="true"]': {
      background: '#F5F0FF',
      color: '#8B5CF6',
    },
  },

  // showMonthYearPicker / showYearPicker render individual month/year cells
  // as .react-datepicker__month-text / __year-text — a different set of
  // classes than the day-grid view above, with their own library defaults
  // (grey keyboard-selected highlight) that need explicit overrides.
  '& .react-datepicker__month-text, & .react-datepicker__year-text': {
    color: '#121826 !important',
    borderRadius: '6px !important',
  },
  '& .react-datepicker__month-text:hover, & .react-datepicker__year-text:hover': {
    backgroundColor: '#F5F0FF !important',
  },
  '& .react-datepicker__month-text--keyboard-selected, & .react-datepicker__year-text--keyboard-selected':
    {
      backgroundColor: '#F5F0FF !important',
      color: '#8B5CF6 !important',
    },
  '& .react-datepicker__month-text--selected, & .react-datepicker__year-text--selected': {
    backgroundColor: '#8B5CF6 !important',
    color: '#ffffff !important',
  },

  '& .react-datepicker__year': {
    '& .react-datepicker__year-wrapper': {
      div: {
        width: '100%',
      },
    },
  },
};

interface Properties {
  value?: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
}

// Modern purple calendar trigger for the Dimensions "Items" table only —
// gated by cell.isModernDate rather than the Plan-page isRevampTable flag,
// since that flag also reads a Plan-page-specific Redux theme selector and
// would fight the font-size/family already tuned for this table.
export const ModernDateInput = forwardRef<HTMLButtonElement, Properties>(
  ({ value, onClick }, reference) => (
    <Button
      ref={reference}
      onClick={onClick}
      width='100%'
      height='100%'
      minH={0}
      variant='ghost'
      border='none'
      boxShadow='none'
      fontSize='13px'
      fontWeight={400}
      fontFamily='Roboto Regular'
      color={value ? '#121826' : '#708090'}
      bg='transparent'
      px={0}
      justifyContent='flex-start'
      overflow='hidden'
      _hover={{ bg: 'transparent' }}
      _active={{ bg: 'transparent' }}
      _focus={{ boxShadow: 'none' }}
    >
      <Box
        as='span'
        overflow='hidden'
        textOverflow='ellipsis'
        whiteSpace='nowrap'
        flex='1'
        textAlign='left'
      >
        {value || ''}
      </Box>
    </Button>
  ),
);

ModernDateInput.displayName = 'ModernDateInput';
